import React, { useState, useRef, useDebugValue, useEffect } from "react";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";

const Card = ({ data, admin, onDelete, time, role, UserKaName }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [Name,setName]=useState("");
  const receiptRef = useRef(null);
  
  const totalWeight = data.reduce((acc, item) => acc + parseFloat(item.weight || 0), 0);
  const totalMoney = data.reduce(
    (acc, item) => acc + parseFloat(String(item.total).replace("₹", "") || 0),
    0
  );
  useEffect(()=>{
    setName(UserKaName);
  },[UserKaName])

  const formatFat = (fat) => (fat > 20 ? `${(fat / 10).toFixed(1)}%` : `${fat}%`);

  const handleDeleteClick = () => {
    if (selectedItem) {
      onDelete(selectedItem.id, time);
      setSelectedItem(null);
    }
  };

  // Helper component for detail rows
  const DetailRow = ({ label, value, highlight }) => (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className={`font-medium text-gray-600 ${highlight ? "text-lg" : ""}`}>{label}:</span>
      <span className={`font-semibold ${highlight ? "text-indigo-600 text-lg" : "text-gray-800"}`}>
        {value}
      </span>
    </div>
  );

  // Detect Android WebView
  const isAndroidWebView = 
    (window.AndroidBridge && typeof window.AndroidBridge.printImage === 'function') ||
    (/Android/i.test(navigator.userAgent));

  // Print function
  const handlePrintEntry = async () => {
    if (isAndroidWebView) {
      try {
        if (!receiptRef.current) {
          toast.error("Receipt element not found");
          return;
        }
        
        const canvas = await html2canvas(receiptRef.current, {
          scale: 2,
          backgroundColor: "#ffffff"
        });
        const base64Image = canvas.toDataURL('image/png');
        
        if (window.AndroidBridge && typeof window.AndroidBridge.printImage === 'function') {
          window.AndroidBridge.printImage(base64Image);
        } else {
          toast.error("Printing not supported in this environment");
        }
      } catch (error) {
        console.error("Printing failed:", error);
        toast.error("Failed to generate receipt image");
      }
    } else {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Popup blocked. Please allow popups for this site.");
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Milk Receipt</title>
            <style>
              body { 
                font-family: 'Courier New', monospace;
                max-width: 300px;
                margin: 0 auto;
                padding: 15px;
                background-color: white;
                color: black;
              }
              .receipt-header {
                text-align: center;
                font-weight: bold;
                font-size: 18px;
                margin-bottom: 10px;
                border-bottom: 1px dashed #000;
                padding-bottom: 5px;
              }
              .receipt-details { margin: 10px 0; }
              .detail-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 5px;
              }
              .divider {
                border-top: 1px dashed #000;
                margin: 10px 0;
              }
              .total-row {
                font-weight: bold;
                font-size: 16px;
              }
              .footer {
                text-align: center;
                margin-top: 20px;
                font-size: 12px;
              }
            </style>
          </head>
          <body>
            <div class="receipt-header">MILK RECEIPT</div>
            <div class="receipt-details">
              <div class="detail-row"><span>Date:</span><span>${selectedItem.date}</span></div>
              <div class="detail-row"><span>User:</span><span>${Name}</span></div>
            </div>
            <div class="divider"></div>
            <div class="receipt-details">
             <div class="detail-row"><span>Animal type:</span><span>${selectedItem.animaltype}</span></div>
               ${selectedItem.snf ? `
    <div class="detail-row">
      <span>Calculation type:</span>
      <span>${"Fat and Snf"}</span>
    </div>
  ` : `    <div class="detail-row">
      <span>Calculation type:</span>
      <span>${"Fat"}</span>
    </div>`}
              <div class="detail-row"><span>Weight:</span><span>${selectedItem.weight} kg</span></div>
              <div class="detail-row"><span>Fat:</span><span>${selectedItem.fat}%</span></div>
              ${selectedItem.snf ? `<div class="detail-row"><span>SNF:</span><span>${selectedItem.snf}%</span></div>` : ''}
              <div class="detail-row"><span>Price per kg:</span><span>₹${selectedItem.price}</span></div>
            </div>
            <div class="divider"></div>
            <div class="detail-row total-row"><span>TOTAL:</span><span>₹${selectedItem.total}</span></div>
            <div class="footer">Generated on ${new Date().toLocaleString()}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  // Hidden receipt for Android printing
  const HiddenReceipt = () => (
    <div 
      ref={receiptRef}
      className="absolute left-[-9999px] top-0 w-[300px] bg-white p-4"
      style={{
        fontFamily: "'Courier New', monospace",
        color: "#000",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)"
      }}
    >
      <div className="text-center font-bold text-lg mb-2 pb-1 border-b border-dashed border-gray-800">
        MILK RECEIPT
      </div>
      <div className="mb-3">
        <div className="flex justify-between mb-1">
          <span>Date:</span>
          <span>{selectedItem?.date}</span>
        </div>
        <div className="flex justify-between mb-1">
          <span>User:</span>
          <span>{Name}</span>
        </div>
      </div>
      <div className="border-t border-dashed border-gray-800 my-2"></div>
      <div className="mb-3">
        <div className="flex justify-between mb-1">
          <span>Weight:</span>
          <span>{selectedItem?.weight} kg</span>
        </div>
        <div className="flex justify-between mb-1">
          <span>Fat:</span>
          <span>{selectedItem?.fat}%</span>
        </div>
        {selectedItem?.snf && (
          <div className="flex justify-between mb-1">
            <span>SNF:</span>
            <span>{selectedItem?.snf}%</span>
          </div>
        )}
        <div className="flex justify-between mb-1">
          <span>Price per kg:</span>
          <span>₹{selectedItem?.price}</span>
        </div>
      </div>
      <div className="border-t border-dashed border-gray-800 my-2"></div>
      <div className="flex justify-between font-bold text-base">
        <span>TOTAL:</span>
        <span>₹{selectedItem?.total}</span>
      </div>
      <div className="text-center text-xs mt-4 text-gray-600">
        Generated on {new Date().toLocaleString()}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mb-16">
      {/* Hidden receipt for Android printing */}
      {selectedItem && <HiddenReceipt />}
      
      {data.map((item, index) => (
        <div
          key={index}
          className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-3 px-5 my-2 shadow-sm hover:shadow-md hover:scale-[1.01] transition-all duration-200 cursor-pointer"
          onClick={() => setSelectedItem(item)}
        >
          {/* Date */}
          <div className="flex justify-between items-center mb-2">
            <div className="font-bold text-indigo-700 text-sm sm:text-lg">{item.date}</div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-4 gap-3 sm:gap-6 text-xs sm:text-sm text-gray-700">
            <div>
              <span className="block font-medium text-gray-500 uppercase tracking-wide">Weight</span>
              <span className="text-gray-900 font-semibold text-sm sm:text-lg">{item.weight} kg</span>
            </div>
            <div className="text-center">
              <span className="block font-medium text-gray-500 uppercase tracking-wide">Fat</span>
              <span className="text-gray-900 font-semibold text-sm sm:text-lg">{formatFat(item.fat)}</span>
            </div>
            <div className="text-center">
              <span className="block font-medium text-gray-500 uppercase tracking-wide">Price</span>
              <span className="text-green-700 font-semibold text-sm sm:text-lg whitespace-nowrap">
                ₹ {item.price}
              </span>
            </div>
            <div className="text-right">
              <span className="block font-medium text-gray-500 uppercase tracking-wide">Total</span>
              <span className="font-bold text-sm sm:text-lg whitespace-nowrap text-blue-700">
                ₹ {item.total}
              </span>
            </div>
          </div>
        </div>
      ))}

      {/* Summary */}
      {totalWeight !== 0 ? (
        <div className="mt-4 mx-4 mb-20 flex justify-between items-center p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg shadow-sm">
          <div className="text-indigo-900 font-bold text-base sm:text-xl whitespace-nowrap">
            {totalWeight.toFixed(2)} kg
          </div>
          <div
            className={`font-bold text-base sm:text-xl whitespace-nowrap ${
              totalMoney >= 0 ? "text-green-700" : "text-red-600"
            }`}
          >
            ₹ {totalMoney.toFixed(2)}
          </div>
        </div>
      ) : (
        <div className="flex w-full justify-center h-[100px] items-center text-gray-500">
          No Entries to show
        </div>
      )}

      {/* Popup */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden transform transition-all duration-200">
            {/* Header */}
            <div className="bg-gray-50 px-5 py-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Entry Details
              </h3>
              <button 
                onClick={() => setSelectedItem(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="px-5 py-4">
              <div className="grid grid-cols-1 gap-3">
                <DetailRow label="Date" value={selectedItem.date} />
                <DetailRow label="Weight" value={`${selectedItem.weight} kg`} />
                <DetailRow label="Fat Content" value={formatFat(selectedItem.fat)} />
                <DetailRow label="Price per kg" value={`₹ ${selectedItem.price}`} />
                <DetailRow 
                  label="Total Amount" 
                  value={`₹ ${selectedItem.total}`} 
                  highlight={true} 
                />
                
                {selectedItem.time && (
                  <DetailRow label="Collection Time" value={selectedItem.time} />
                )}
                
                {selectedItem.animalType && (
                  <DetailRow label="Animal Type" value={selectedItem.animalType} />
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-gray-50 px-5 py-4 border-t flex flex-wrap gap-3 justify-between">
              <button
                onClick={handlePrintEntry}
                className="px-4 py-2 flex items-center gap-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                </svg>
                Print Receipt
              </button>
              
              <div className="flex gap-2">
                <button
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                  onClick={() => setSelectedItem(null)}
                >
                  Close
                </button>
                
                {role !== "associated user" && (
                  <button
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition flex items-center gap-2"
                    onClick={handleDeleteClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;